import React, { useEffect, useState, useRef } from 'react';
import { XIcon, UserIcon, CalendarIcon, ClipboardIcon, ClockIcon, PencilIcon, TrashIcon, ExternalLinkIcon, CheckIcon, XCircleIcon, AlertCircleIcon } from 'lucide-react';
import { getTaskDisplayStatus } from './taskUtils';
import { localISOToUtcISO, LOCAL_TZ } from '../../src/utils/time';
import { ymdLocal } from '../../src/utils/datetime';
// Task types
type TaskStatus = 'pending' | 'in-progress' | 'completed';
interface AssignedTask {
  id: string;
  name?: string;
  title?: string;
  studentId: string;
  studentName: string;
  studentAvatarUrl?: string;
  deadline?: string;
  dueDate?: string;
  dueAt?: string;
  status: TaskStatus;
  notes?: string;
  createdAt?: string;
  reference?: string;
  startDate?: string;
}
interface TaskDetailsPanelProps {
  open: boolean;
  task?: AssignedTask | null;
  onClose: () => void;
  onEditClick?: (taskId: string) => void;
  onDeleteClick?: (taskId: string) => void;
  onSaveEdit?: (taskId: string, updates: {
    title: string;
    dueAt?: string | null;
    status: 'pending' | 'in-progress' | 'completed';
    notes?: string | null;
    reference?: string | null;
  }) => void;
  darkMode: boolean;
  isEditMode?: boolean;
  onExitEditMode?: () => void;
}
export const TaskDetailsPanel: React.FC<TaskDetailsPanelProps> = ({
  open,
  task,
  onClose,
  onEditClick,
  onDeleteClick,
  onSaveEdit,
  darkMode,
  isEditMode = false,
  onExitEditMode
}) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [animation, setAnimation] = useState<'entering' | 'entered' | 'exiting' | 'exited'>('exited');
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editedReason, setEditedReason] = useState('');
  const [editedDetails, setEditedDetails] = useState('');
  const [editedPracticeHours, setEditedPracticeHours] = useState<number | undefined>(undefined);
  // Local state for displayed values (updates after save)
  const [displayedReason, setDisplayedReason] = useState('');
  const [displayedDetails, setDisplayedDetails] = useState('');
  const [displayedPracticeHours, setDisplayedPracticeHours] = useState<number | undefined>(undefined);
  const lastActiveElement = useRef<HTMLElement | null>(null);
  // State for copy feedback
  const [isCopied, setIsCopied] = useState(false);
  // Remove local isEditMode state - now using prop from parent
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  // Form values for edit mode
  const [formValues, setFormValues] = useState({
    title: '',
    dueDate: '',
    dueTime: '',
    status: 'pending' as TaskStatus,
    notes: '',
    reference: ''
  });
  // Track if form has unsaved changes
  const [isDirty, setIsDirty] = useState(false);
  // Validation state
  const [errors, setErrors] = useState<{
    title?: string;
    dueDate?: string;
  }>({});
  // Handle animation states
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (open && animation !== 'entered') {
      // Save the currently focused element to restore focus later
      lastActiveElement.current = document.activeElement as HTMLElement;
      setAnimation('entering');
      timer = setTimeout(() => {
        setAnimation('entered');
      }, 50); // Small delay to ensure the entering class is applied
    } else if (!open && animation !== 'exited') {
      setAnimation('exiting');
      timer = setTimeout(() => {
        setAnimation('exited');
      }, 300); // Match the CSS transition duration
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [open, animation]);
  // Reset when panel closes
  useEffect(() => {
    if (!open) {
      setShowDiscardConfirm(false);
      setShowDeleteConfirm(false);
      setIsDirty(false);
    }
  }, [open]);
  // Initialize form values when task changes or edit mode is entered
  useEffect(() => {
    if (task && isEditMode) {
      // Split date and time from ISO string for separate inputs
      let dueDate = '';
      let dueTime = '';
      const dueAt = task.dueAt || task.deadline || task.dueDate;
      if (dueAt) {
        try {
          // Use ymdLocal to extract the date in local timezone
          dueDate = ymdLocal(dueAt, LOCAL_TZ);
          // For time, convert to local date
          const date = new Date(dueAt);
          if (!isNaN(date.getTime())) {
            dueTime = date.toTimeString().slice(0, 5);
          }
        } catch (err) {
          console.error('Error parsing due date:', err);
        }
      }
      setFormValues({
        title: task.title || task.name || '',
        dueDate,
        dueTime,
        status: task.status || 'pending',
        notes: task.notes || '',
        reference: task.reference || ''
      });
      setIsDirty(false);
      setErrors({});
    }
  }, [task, isEditMode]);
  // Handle loading state
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (open) {
      setIsLoading(true);
      timer = setTimeout(() => {
        setIsLoading(task === undefined || task === null);
      }, 200); // Show loading state if task is still null after 200ms
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [open, task]);
  // Handle escape key press
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && open) {
        // If delete confirmation is showing, close it
        if (showDeleteConfirm) {
          setShowDeleteConfirm(false);
          return;
        }
        if (isEditMode && isDirty) {
          // Show confirmation dialog if there are unsaved changes
          setShowDiscardConfirm(true);
        } else if (isEditMode) {
          // Exit edit mode if no changes
          setIsEditMode(false);
        } else {
          // Close panel if not in edit mode
          onClose();
        }
      }
    };
    document.addEventListener('keydown', handleEscapeKey);
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [open, onClose, isEditMode, isDirty, showDeleteConfirm]);
  // Handle Enter key for delete confirmation modal
  useEffect(() => {
    const handleEnterKey = (event: KeyboardEvent) => {
      // Only handle Enter when delete confirmation modal is open
      if (event.key === 'Enter' && showDeleteConfirm) {
        // Prevent if user is typing in an input field (shouldn't be any in this modal, but safety check)
        const target = event.target as HTMLElement;
        if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
          return;
        }
        event.preventDefault();
        handleDeleteConfirm();
      }
    };
    document.addEventListener('keydown', handleEnterKey);
    return () => {
      document.removeEventListener('keydown', handleEnterKey);
    };
  }, [showDeleteConfirm, task]);
  // Restore focus when panel closes
  useEffect(() => {
    if (!open && lastActiveElement.current) {
      setTimeout(() => {
        lastActiveElement.current?.focus();
      }, 300); // Match the CSS transition duration
    }
  }, [open]);
  // Focus trap inside the panel
  useEffect(() => {
    if (!open || !panelRef.current) return;
    const panel = panelRef.current;
    const focusableElements = panel.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
    if (focusableElements.length === 0) return;
    const firstElement = focusableElements[0] as HTMLElement;
    const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;
    // Focus the first element when panel opens
    if (animation === 'entered' && !isEditMode) {
      firstElement.focus();
    }
    const handleTabKey = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return;
      if (event.shiftKey) {
        if (document.activeElement === firstElement) {
          lastElement.focus();
          event.preventDefault();
        }
      } else {
        if (document.activeElement === lastElement) {
          firstElement.focus();
          event.preventDefault();
        }
      }
    };
    panel.addEventListener('keydown', handleTabKey);
    return () => {
      panel.removeEventListener('keydown', handleTabKey);
    };
  }, [open, animation, isEditMode]);
  // If panel is not open or exited, don't render anything
  if (animation === 'exited' && !open) return null;
  // Enter edit mode - now only calls parent callback
  const handleEnterEditMode = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Only call parent callback to enter edit mode
    if (onEditClick && task) {
      onEditClick(task.id);
    }
  };
  // Cancel edit mode
  const handleCancelEdit = () => {
    if (isDirty) {
      setShowDiscardConfirm(true);
    } else {
      // Exit edit mode via parent callback
      if (onExitEditMode) {
        onExitEditMode();
      } else {
        // Fallback: close the panel
        onClose();
      }
    }
  };
  // Confirm discard changes
  const handleConfirmDiscard = () => {
    setShowDiscardConfirm(false);
    setIsDirty(false);
    // Exit edit mode via parent callback
    if (onExitEditMode) {
      onExitEditMode();
    } else {
      // Fallback: close the panel
      onClose();
    }
  };
  // Handle form input changes
  const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormValues(prev => ({
      ...prev,
      [name]: value
    }));
    setIsDirty(true);
    // Clear validation error when field is updated
    if (name === 'title' && errors.title) {
      setErrors(prev => ({
        ...prev,
        title: undefined
      }));
    }
  };
  // Validate the form before saving
  const validateForm = () => {
    const newErrors: {
      title?: string;
      dueDate?: string;
    } = {};
    if (!formValues.title.trim()) {
      newErrors.title = 'Title is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  // Handle form submission
  const handleSaveChanges = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm() || !task) return;
    // Prepare dates for saving using timezone utilities
    let dueAt: string | null = null;
    if (formValues.dueDate && formValues.dueDate.trim()) {
      // Append midnight time to treat as local date
      const localMidnight = formValues.dueDate + 'T00:00:00';
      // Convert to UTC using timezone utilities
      dueAt = localISOToUtcISO(localMidnight, LOCAL_TZ);
    }
    // Call the save callback with updated values
    if (onSaveEdit) {
      onSaveEdit(task.id, {
        title: formValues.title,
        dueAt,
        status: formValues.status,
        notes: formValues.notes || null,
        reference: formValues.reference || null
      });
    }
    // Exit edit mode via parent callback
    setIsDirty(false);
    if (onExitEditMode) {
      onExitEditMode();
    }
  };
  // Handle copy Task ID to clipboard
  const handleCopyTaskId = async () => {
    if (!task?.id) return;
    try {
      await navigator.clipboard.writeText(task.id);
      setIsCopied(true);
      // Reset after 2 seconds
      setTimeout(() => {
        setIsCopied(false);
      }, 2000);
    } catch (err) {
      console.error('Failed to copy Task ID:', err);
    }
  };
  // Format date for display
  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '—';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };
  // Format time for display
  const formatTime = (dateStr?: string) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  // Format date and time together
  const formatDateTime = (dateStr?: string) => {
    if (!dateStr) return '—';
    // Only show the date, not the time
    return formatDate(dateStr);
  };
  // Get status badge style based on status - UPDATED to use computed status
  const getStatusBadge = (status?: TaskStatus, task?: AssignedTask | null) => {
    // If we have the full task object, use computed status
    if (task) {
      const displayStatus = getTaskDisplayStatus(task);
      switch (displayStatus) {
        case 'completed':
          return darkMode ? 'bg-green-900/30 text-green-200' : 'bg-green-100 text-green-800';
        case 'overdue':
          return darkMode ? 'bg-red-900/30 text-red-200' : 'bg-red-100 text-red-800';
        case 'in-progress':
          return darkMode ? 'bg-yellow-900/30 text-yellow-200' : 'bg-yellow-100 text-yellow-800';
        case 'pending':
        default:
          return darkMode ? 'bg-blue-900/30 text-blue-200' : 'bg-blue-100 text-blue-800';
      }
    }
    // Fallback to original logic if we only have status string
    switch (status) {
      case 'completed':
        return darkMode ? 'bg-green-900/30 text-green-200' : 'bg-green-100 text-green-800';
      case 'in-progress':
        return darkMode ? 'bg-yellow-900/30 text-yellow-200' : 'bg-yellow-100 text-yellow-800';
      case 'pending':
      default:
        return darkMode ? 'bg-blue-900/30 text-blue-200' : 'bg-blue-100 text-blue-800';
    }
  };
  // Get human-readable status text - UPDATED to use computed status
  const getStatusText = (status?: TaskStatus, task?: AssignedTask | null) => {
    // If we have the full task object, use computed status
    if (task) {
      const displayStatus = getTaskDisplayStatus(task);
      switch (displayStatus) {
        case 'completed':
          return 'Completed';
        case 'overdue':
          return 'Overdue';
        case 'in-progress':
          return 'In Progress';
        case 'pending':
        default:
          return 'Pending';
      }
    }
    // Fallback to original logic if we only have status string
    switch (status) {
      case 'completed':
        return 'Completed';
      case 'in-progress':
        return 'In Progress';
      case 'pending':
      default:
        return 'Pending';
    }
  };
  // Get status icon color based on computed status
  const getStatusIconColor = (task?: AssignedTask | null) => {
    if (!task) return '#9CA3AF'; // Default gray
    const displayStatus = getTaskDisplayStatus(task);
    switch (displayStatus) {
      case 'completed':
        return '#22C55E';
      // Green
      case 'overdue':
        return '#EF4444';
      // Red
      case 'in-progress':
        return '#EAB308';
      // Yellow
      case 'pending':
      default:
        return '#3B82F6';
      // Blue
    }
  };
  // Check if task is overdue
  const isOverdue = (dateStr?: string) => {
    if (!dateStr) return false;
    const dueDate = new Date(dateStr);
    const now = new Date();
    return dueDate < now && task?.status !== 'completed';
  };
  // Get the correct due date from various possible fields
  const getDueDate = () => {
    return task?.dueAt || task?.deadline || task?.dueDate;
  };
  // Prevent closing panel when clicking on the panel itself
  const handlePanelClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };
  // Handle delete confirmation
  const handleDeleteConfirm = () => {
    if (onDeleteClick && task) {
      onDeleteClick(task.id);
    }
    setShowDeleteConfirm(false);
  };
  return <div className={`fixed inset-0 z-50 ${animation === 'exited' ? 'pointer-events-none' : ''}`} aria-modal="true" role="dialog" aria-labelledby="task-details-title" data-id="element-11011">
      {/* Overlay - Fixed to only close when clicked directly */}
      <div className={`absolute inset-0 bg-black transition-opacity duration-300 ${animation === 'entering' || animation === 'entered' ? 'opacity-50' : 'opacity-0'}`} onClick={e => {
      // Only close if clicked directly on the overlay (not when event bubbles up)
      if (e.target === e.currentTarget) {
        if (isEditMode && isDirty) {
          setShowDiscardConfirm(true);
        } else if (!isEditMode) {
          onClose();
        }
      }
    }} data-id="element-11012" />
      {/* Panel */}
      <div ref={panelRef} className={`absolute inset-y-0 right-0 w-full max-w-[480px] ${darkMode ? 'bg-navy-800 text-white' : 'bg-white text-gray-800'} shadow-xl flex flex-col transform transition-transform duration-500 ${animation === 'entering' || animation === 'entered' ? 'translate-x-0' : 'translate-x-full'} md:rounded-l-2xl`} onClick={handlePanelClick} data-id="element-11013">
        {/* Panel Header */}
        <div className={`px-6 py-4 flex items-center justify-between border-b ${darkMode ? 'border-navy-700' : 'border-gray-200'}`} data-id="element-11014">
          <h2 id="task-details-title" className="text-xl font-semibold truncate flex-1 pr-4" data-id="element-11015">
            {isLoading ? <div className={`h-7 w-3/4 rounded ${darkMode ? 'bg-navy-700' : 'bg-gray-200'} animate-pulse`} data-id="element-11016"></div> : isEditMode ? 'Edit Task' : task?.title || task?.name || 'Task Details'}
          </h2>
          <button type="button" onClick={e => {
          e.stopPropagation();
          if (isEditMode && isDirty) {
            setShowDiscardConfirm(true);
          } else {
            onClose();
          }
        }} className={`p-2 rounded-full ${darkMode ? 'hover:bg-navy-700 text-gray-400 hover:text-white' : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'} transition-colors`} aria-label="Close panel" data-id="element-11017">
            <XIcon className="h-5 w-5" data-id="element-11018" />
          </button>
        </div>
        {/* Discard Changes Confirmation */}
        {showDiscardConfirm && <div className="p-4 mx-6 mt-6 rounded-lg bg-red-500/10 border border-red-500/30" data-id="element-11019">
            <p className={`text-sm ${darkMode ? 'text-white' : 'text-gray-800'} mb-3`} data-id="element-11020">
              <AlertCircleIcon className="inline-block h-4 w-4 mr-2 text-red-500" data-id="element-11021" />
              Discard your unsaved changes?
            </p>
            <div className="flex justify-end space-x-3" data-id="element-11022">
              <button type="button" onClick={e => {
            e.stopPropagation();
            setShowDiscardConfirm(false);
          }} className={`px-3 py-1.5 rounded-lg text-sm ${darkMode ? 'bg-navy-700 text-white hover:bg-navy-600' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'}`} data-id="element-11023">
                Cancel
              </button>
              <button type="button" onClick={e => {
            e.stopPropagation();
            handleConfirmDiscard();
          }} className="px-3 py-1.5 rounded-lg text-sm bg-red-600 text-white hover:bg-red-700" data-id="element-11024">
                Discard
              </button>
            </div>
          </div>}
        {/* Panel Content */}
        <div className="flex-1 overflow-y-auto" data-id="element-11025">
          {isLoading ? <div className="p-6 space-y-6" data-id="element-11026">
              <div className={`h-12 rounded ${darkMode ? 'bg-navy-700' : 'bg-gray-200'} animate-pulse`} data-id="element-11027"></div>
              <div className={`h-12 rounded ${darkMode ? 'bg-navy-700' : 'bg-gray-200'} animate-pulse`} data-id="element-11028"></div>
              <div className={`h-24 rounded ${darkMode ? 'bg-navy-700' : 'bg-gray-200'} animate-pulse`} data-id="element-11029"></div>
            </div> : task ? <form onSubmit={handleSaveChanges} className="p-6 space-y-6" data-id="element-11030">
              {/* Assignee Section - Always read-only */}
              <div data-id="element-11031">
                <h3 className={`text-sm font-medium mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-500'}`} data-id="element-11032">
                  Assigned To
                </h3>
                <div className="flex items-center cursor-pointer group" onClick={() => console.log('open student profile', task.studentId)} data-id="element-11033">
                  <div className={`h-10 w-10 rounded-full flex items-center justify-center mr-3 ${darkMode ? 'bg-navy-700' : 'bg-gray-200'}`} data-id="element-11034">
                    {task.studentAvatarUrl ? <img src={task.studentAvatarUrl} alt={task.studentName} className="h-10 w-10 rounded-full object-cover" data-id="element-11035" /> : <UserIcon className="h-5 w-5 text-[#3B82F6] group-hover:opacity-[1.15] transition-opacity" data-id="element-11036" />}
                  </div>
                  <div data-id="element-11037">
                    <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-800'} group-hover:text-deep-pink transition-colors`} data-id="element-11038">
                      {task.studentName || 'Unknown Student'}
                    </p>
                    <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-11039">
                      Student ID: {task.studentId}
                    </p>
                  </div>
                </div>
              </div>
              {/* Title Section - Editable in edit mode */}
              {isEditMode && <div data-id="element-11040">
                  <label htmlFor="task-title" className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-500'}`} data-id="element-11041">
                    Task Title <span className="text-red-500" data-id="element-11042">*</span>
                  </label>
                  <input type="text" id="task-title" name="title" value={formValues.title} onChange={handleInputChange} className={`w-full px-3 py-2 rounded-lg ${darkMode ? 'bg-navy-900/50 border-navy-700 text-white focus:ring-deep-pink focus:border-deep-pink' : 'bg-white border-gray-300 text-gray-900 focus:ring-deep-pink focus:border-deep-pink'} border ${errors.title ? 'border-red-500' : ''}`} placeholder="Enter task title" data-id="element-11043" />
                  {errors.title && <p className="mt-1 text-xs text-red-500" data-id="element-11044">{errors.title}</p>}
                </div>}
              {/* Due Date & Status Section */}
              <div className={`pb-6 border-b ${darkMode ? 'border-navy-700' : 'border-gray-200'}`} data-id="element-11045">
                <h3 className={`text-sm font-medium mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-500'}`} data-id="element-11046">
                  Task Timeline & Status
                </h3>
                <div className="flex flex-col space-y-4" data-id="element-11047">
                  {/* Due Date */}
                  {isEditMode ? <div className="space-y-3" data-id="element-11048">
                      <div data-id="element-11049">
                        <label htmlFor="task-due-date" className={`block text-sm mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-11050">
                          Due Date
                        </label>
                        <input type="date" id="task-due-date" name="dueDate" value={formValues.dueDate} onChange={handleInputChange} className={`w-full px-3 py-2 rounded-lg ${darkMode ? 'bg-navy-900/50 border-navy-700 text-white focus:ring-deep-pink focus:border-deep-pink' : 'bg-white border-gray-300 text-gray-900 focus:ring-deep-pink focus:border-deep-pink'} border ${errors.dueDate ? 'border-red-500' : ''}`} data-id="element-11051" />
                        {errors.dueDate && <p className="mt-1 text-xs text-red-500" data-id="element-11052">
                            {errors.dueDate}
                          </p>}
                      </div>
                    </div> : <div className="flex items-start group" data-id="element-11053">
                      <CalendarIcon className="h-5 w-5 mt-0.5 mr-3 text-[#EF4444] group-hover:opacity-[1.15] transition-opacity" data-id="element-11054" />
                      <div data-id="element-11055">
                        <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-800'}`} data-id="element-11056">
                          {isOverdue(getDueDate()) ? <span className="text-red-500" data-id="element-11057">Overdue</span> : 'Due Date'}
                        </p>
                        <p className={`${darkMode ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-11058">
                          {formatDateTime(getDueDate())}
                        </p>
                      </div>
                    </div>}
                  {/* Status - Editable in edit mode */}
                  {!isEditMode && <div className="flex items-center group" data-id="element-11059">
                      <ClipboardIcon className="h-5 w-5 mr-3 text-[#22C55E] group-hover:opacity-[1.15] transition-opacity" data-id="element-11060" />
                      <div data-id="element-11061">
                        <p className={`font-medium mb-1.5 ${darkMode ? 'text-white' : 'text-gray-800'}`} data-id="element-11062">
                          Status
                        </p>
                        <span className={`px-3 py-1 rounded-full text-sm ${getStatusBadge(undefined, task)}`} data-id="element-11063">
                          {getStatusText(undefined, task)}
                        </span>
                      </div>
                    </div>}
                </div>
              </div>
              {/* Notes Section - Editable in edit mode */}
              <div className={`pb-6 border-b ${darkMode ? 'border-navy-700' : 'border-gray-200'}`} data-id="element-11064">
                <h3 className={`text-sm font-medium mb-3 ${darkMode ? 'text-white' : 'text-gray-800'}`} data-id="element-11065">
                  Notes
                </h3>
                {isEditMode ? <textarea id="task-notes" name="notes" value={formValues.notes} onChange={handleInputChange} rows={4} className={`w-full px-3 py-2 rounded-lg resize-none ${darkMode ? 'bg-navy-900/50 border-navy-700 text-white focus:ring-deep-pink focus:border-deep-pink' : 'bg-white border-gray-300 text-gray-900 focus:ring-deep-pink focus:border-deep-pink'} border`} placeholder="Add notes about this task (optional)" data-id="element-11066" /> : task.notes ? <div className={`whitespace-pre-line ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-11067">
                    {task.notes}
                  </div> : <p className={`italic ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-11068">
                    No notes added
                  </p>}
              </div>
              {/* Reference URL Field - Add in edit mode */}
              <div className={`pb-6 border-b ${darkMode ? 'border-navy-700' : 'border-gray-200'}`} data-id="element-11069">
                <h3 className={`text-sm font-medium mb-3 ${darkMode ? 'text-white' : 'text-gray-800'}`} data-id="element-11070">
                  Reference
                </h3>
                {isEditMode ? <input type="text" id="task-reference" name="reference" value={formValues.reference} onChange={handleInputChange} className={`w-full px-3 py-2 rounded-lg ${darkMode ? 'bg-navy-900/50 border-navy-700 text-white focus:ring-deep-pink focus:border-deep-pink' : 'bg-white border-gray-300 text-gray-900 focus:ring-deep-pink focus:border-deep-pink'} border`} placeholder="https://..." data-id="element-11071" /> : task.reference ? <a href={task.reference} target="_blank" rel="noopener noreferrer" className={`inline-flex items-center ${darkMode ? 'text-deep-pink hover:text-burgundy' : 'text-deep-pink hover:text-burgundy'} transition-colors`} data-id="element-11072">
                    <ExternalLinkIcon className="h-4 w-4 mr-2" data-id="element-11073" />
                    Open reference link
                  </a> : <p className={`italic ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-11074">
                    No reference link added
                  </p>}
              </div>
              {/* Meta Section - Read-only */}
              {!isEditMode && <div data-id="element-11075">
                  <h3 className={`text-sm font-medium mb-3 ${darkMode ? 'text-white' : 'text-gray-800'}`} data-id="element-11076">
                    Details
                  </h3>
                  <div className="space-y-3" data-id="element-11077">
                    {task.createdAt && <div className="flex items-start group" data-id="element-11078">
                        <ClockIcon className="h-4 w-4 mt-0.5 mr-2 text-[#8B5CF6] group-hover:opacity-[1.15] transition-opacity" data-id="element-11079" />
                        <div data-id="element-11080">
                          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-11081">
                            Created
                          </p>
                          <p className={`text-sm ${darkMode ? 'text-white' : 'text-gray-800'}`} data-id="element-11082">
                            {formatDateTime(task.createdAt)}
                          </p>
                        </div>
                      </div>}
                    <div className="flex items-start group" data-id="element-11083">
                      <div className="h-4 w-4 mt-0.5 mr-2 flex items-center justify-center text-[#8B5CF6] group-hover:opacity-[1.15] transition-opacity" data-id="element-11084">
                        #
                      </div>
                      <div className="flex-1" data-id="element-11085">
                        <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-11086">
                          Task ID
                        </p>
                        <p className={`text-sm font-mono ${darkMode ? 'text-white' : 'text-gray-800'}`} data-id="element-11087">
                          {task.id}
                        </p>
                      </div>
                      <div className="flex items-center gap-2" data-id="element-11088">
                        <button type="button" onClick={handleCopyTaskId} className={`p-1 rounded ${darkMode ? 'hover:bg-navy-700' : 'hover:bg-gray-100'} transition-colors`} title={isCopied ? 'Copied!' : 'Copy to clipboard'} data-id="element-11089">
                          {isCopied ? <CheckIcon className="h-3.5 w-3.5 text-green-500" data-id="element-11090" /> : <ClipboardIcon className="h-3.5 w-3.5" data-id="element-11091" />}
                        </button>
                        {isCopied && <span className="text-xs text-green-500 font-medium whitespace-nowrap animate-in fade-in duration-200" data-id="element-11092">
                            Task ID copied
                          </span>}
                      </div>
                    </div>
                  </div>
                </div>}
            </form> : <div className="p-6 text-center" data-id="element-11093">
              <p className={darkMode ? 'text-gray-400' : 'text-gray-500'} data-id="element-11094">
                No task data available
              </p>
            </div>}
        </div>
        {/* Panel Footer */}
        {!isLoading && task && <div className={`p-6 border-t ${darkMode ? 'border-navy-700' : 'border-gray-200'} flex justify-between`} data-id="element-11095">
            {isEditMode ? <>
                <button type="button" onClick={e => {
            e.stopPropagation();
            handleCancelEdit();
          }} className={`px-4 py-2 rounded-lg border ${darkMode ? 'border-gray-600 text-gray-300 hover:bg-navy-700' : 'border-gray-300 text-gray-700 hover:bg-gray-100'} transition-colors flex items-center`} data-id="element-11096">
                  <XCircleIcon className="h-4 w-4 mr-2" data-id="element-11097" />
                  Cancel
                </button>
                <button type="button" onClick={e => {
            e.stopPropagation();
            handleSaveChanges(e);
          }} disabled={!formValues.title.trim()} className={`px-4 py-2 rounded-md border-2 flex items-center transition-colors ${!formValues.title.trim() ? 'border-gray-400 text-gray-400 cursor-not-allowed' : darkMode ? 'border-[#10B981] text-[#10B981] hover:bg-[#10B981]/10' : 'border-[#10B981] text-[#10B981] hover:bg-[#10B981]/5'}`} data-id="element-11098">
                  <CheckIcon className="h-4 w-4 mr-1" data-id="element-11099" />
                  Save Changes
                </button>
              </> : <>
                <button type="button" onClick={e => {
            e.stopPropagation();
            setShowDeleteConfirm(true);
          }} className={`px-4 py-2 rounded-lg flex items-center ${darkMode ? 'text-red-400 hover:bg-red-900/20' : 'text-red-600 hover:bg-red-50'} transition-colors`} data-id="element-11100">
                  <TrashIcon className="h-4 w-4 mr-2" data-id="element-11101" />
                  Delete
                </button>
                <button type="button" onClick={handleEnterEditMode} className={`px-4 py-2 rounded-md border-2 flex items-center transition-colors ${darkMode ? 'border-[#10B981] text-[#10B981] hover:bg-[#10B981]/10' : 'border-[#10B981] text-[#10B981] hover:bg-[#10B981]/5'}`} data-id="element-11102">
                  <PencilIcon className="h-4 w-4 mr-1" data-id="element-11103" />
                  Edit Task
                </button>
              </>}
          </div>}
      </div>
      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && <div className="fixed inset-0 z-60 overflow-y-auto" data-id="element-11104">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center" data-id="element-11105">
            <div className="fixed inset-0 transition-opacity" onClick={() => setShowDeleteConfirm(false)} data-id="element-11106">
              <div className={`absolute inset-0 ${darkMode ? 'bg-navy-900' : 'bg-gray-900'} opacity-75`} data-id="element-11107"></div>
            </div>
            <div className={`inline-block align-bottom rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full ${darkMode ? 'bg-navy-800 text-white' : 'bg-white text-gray-800'}`} data-id="element-11108">
              <div className="p-6" data-id="element-11109">
                <h3 className="text-lg font-medium mb-4" data-id="element-11110">
                  Are you sure you want to delete this task?
                </h3>
                <div className="flex justify-end space-x-3" data-id="element-11111">
                  <button type="button" onClick={() => setShowDeleteConfirm(false)} className={`px-4 py-2 rounded-lg border ${darkMode ? 'border-gray-600 text-gray-300 hover:bg-navy-700' : 'border-gray-300 text-gray-700 hover:bg-gray-100'} transition-colors`} data-id="element-11112">
                    Cancel
                  </button>
                  <button type="button" onClick={handleDeleteConfirm} className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white transition-colors" data-id="element-11113">
                    Yes, Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>}
    </div>;
};